import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import submit from "submit-form";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { LiveFeed } from "./LiveFeed";
import { AsideGraphSkeleton } from "./skeletons/AsideGraphSkeleton";
import SvgIcon from "../../../../../parts/SvgIcon";

import useClickOutside from "../../../../../hooks/useClickOutside";

const asideDateRanges = [
  { label: "Last 12 Months", value: 12, key: "twelve-months" },
  { label: "Last 24 Months", value: 24, key: "twenty-four-months" },
  { label: "Last 36 Months", value: 36, key: "thirty-six-months" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="program-metrics custom-tooltip">
        <p className="label">{`${payload[0].payload?.monthLong} ${payload[0].payload?.year}`}</p>
        <p className="value">{payload[0]?.value}</p>
      </div>
    );
  }
  return null;
};

export const AsideStats = ({ groupedMetricsByProgram, locals }) => {
  const { selectedProgramMetrics, query } = locals;
  const { selectedProgramId, monthFrom } = query;
  const [opened, setOpened] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const dropdownRef = useRef(null);
  useClickOutside(
    dropdownRef,
    () => {
      setOpened(false);
    },
    opened
  );

  // Get the corresponding date range object for the selected month using the keys
  const selectedDateRange =
    asideDateRanges.find(({ key }) => key === monthFrom) || asideDateRanges[0];

  const loadChartData = () => {
    const groupedMetricsByMonth = selectedProgramMetrics?.reduce(
      (acc, curr) => {
        const { utcDate } = curr;
        const monthShort = moment(utcDate, "YYYYMMDD").format("MMM");
        const monthLong = moment(utcDate, "YYYYMMDD").format("MMMM");
        const year = moment(utcDate, "YYYYMMDD").format("YYYY");
        const monthNumber = moment(utcDate, "YYYYMMDD").format("M");
        const monthYear = moment(utcDate, "YYYYMMDD").format("MMMM YYYY");

        if (acc[`${monthShort}-${year}`]) {
          acc[`${monthShort}-${year}`].count += 1;
        } else {
          acc[`${monthShort}-${year}`] = {
            count: 1,
            monthShort,
            monthLong,
            year,
            monthNumber,
            monthYear,
          };
        }
        return acc;
      },
      {}
    );

    const sortedGraphData = Object.values(groupedMetricsByMonth).sort(
      (a, b) => {
        if (a.year < b.year) {
          return -1;
        } else if (a.year > b.year) {
          return 1;
        } else {
          if (Number(a.monthNumber) < Number(b.monthNumber)) {
            return -1;
          } else if (Number(a.monthNumber) > Number(b.monthNumber)) {
            return 1;
          } else {
            return 0;
          }
        }
      }
    );

    setChartData(sortedGraphData);
  };

  useEffect(() => {
    if (selectedProgramMetrics && loading) {
      setLoading(false);
    }
  }, [selectedProgramMetrics]);

  useEffect(() => {
    loadChartData(selectedProgramMetrics);
  }, [selectedProgramMetrics, monthFrom]);

  return (
    <aside role="program-metrics" className="mobile-hidden">
      <div role="program-usage-chart">
        <header>
          <div>
            <span>
              {groupedMetricsByProgram[selectedProgramId]?.programName}
            </span>
            <span>Program Usage Chart</span>
          </div>
          <details open={true} ref={dropdownRef} role="list">
            <summary
              aria-haspopup="listbox"
              onClick={(e) => {
                e.preventDefault();
                setOpened(!opened);
              }}
            >
              <span>{selectedDateRange.label}</span>
              <SvgIcon src={`/img/svg/angle-${opened ? "up" : "down"}.svg`} />
            </summary>
            <div className={opened ? "opened" : undefined}>
              {asideDateRanges?.map(({ label, value, key }) => {
                return (
                  <button
                    key={key}
                    onClick={(e) => {
                      setOpened(false);
                      e.preventDefault();
                      submit("/actions/refresh", {
                        method: "GET",
                        body: {
                          monthFrom: key,
                        },
                      });
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </details>
        </header>
        {loading ? (
          <AsideGraphSkeleton locals={locals} />
        ) : (
          <div role="graph-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={"100%"}
                height={"100%"}
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                barSize={20}
              >
                <XAxis
                  dataKey={chartData?.length > 12 ? "monthYear" : "monthShort"}
                  {...(chartData?.length > 12 && {
                    ticks: [
                      chartData[0].monthYear,
                      chartData[chartData.length - 1 || 0].monthYear,
                    ],
                  })}
                  tick={{ fill: "var(--white-70)" }}
                />
                <YAxis
                  tick={{ fill: "var(--white-70)" }}
                  tickMargin={10}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "var(--white-10)" }}
                  content={<CustomTooltip />}
                />
                <CartesianGrid
                  vertical={false}
                  stroke="var(--white-05-solid)"
                />
                <Bar
                  dataKey="count"
                  fill="var(--white-20-solid)"
                  activeBar={<Rectangle fill="var(--primary)" />}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <LiveFeed locals={locals} />
    </aside>
  );
};
